import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import { Sparkles, FolderGit2, Bot, ArrowRight } from 'lucide-react';
import './EmployeeDashboard.css';

const EmployeeDashboard = () => {
  const { user, employee, onboardingStatus } = useAuth();
  const navigate = useNavigate();
  const [hrProfile, setHrProfile] = useState(null);

  const firstName = user?.first_name || user?.username || 'Team Member';
  const progressPercent = onboardingStatus?.progress_percentage || 0;

  useEffect(() => {
    api.get('/hr/active/')
      .then(res => setHrProfile(res.data))
      .catch(err => console.error(err));
  }, []);

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content">
          
          {/* Welcome Hero Banner */}
          {(() => {
            const deptName = employee?.department_detail?.name;
            const deptAvatar = employee?.department_detail?.avatar_image;
            const deptCover = employee?.department_detail?.department_image;

            return (
              <div
                className="dashboard-hero-banner"
                style={{
                  background: deptCover
                    ? `linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 41, 59, 0.85) 100%), url(${deptCover}) center/cover no-repeat`
                    : 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4338ca 100%)'
                }}
              >
                <div className="dashboard-hero-left">
                  <div className="badge dashboard-hero-badge">
                    <Sparkles size={12} /> MentorAI Joinee Workspace
                  </div>
                  <h1 className="dashboard-hero-title">
                    Welcome to the team, {firstName}! 👋
                  </h1>
                  <p className="dashboard-hero-subtitle">
                    Let's get you familiar with company policies, {deptName ? `${deptName} department standards` : 'department standards'}, and assigned project repositories. Ask your AI Lead whenever you need guidance.
                  </p>
                </div>

                {/* Assigned Department AI Lead Card */}
                {deptName && (
                  <div className="dashboard-hero-trainer-card">
                    <div className="dashboard-hero-avatar-box">
                      {deptAvatar ? (
                        <img src={deptAvatar} alt="Dept AI Lead" className="dashboard-hero-avatar-img" />
                      ) : (
                        <Bot size={34} style={{ color: '#059669' }} />
                      )}
                    </div>
                    <span className="dashboard-hero-trainer-tag">
                      Assigned AI Lead
                    </span>
                    <div className="dashboard-hero-trainer-name">
                      {deptName}
                    </div>
                    <span className="dashboard-hero-trainer-status">
                      ● Online & Assisting You
                    </span>
                  </div>
                )}
              </div>
            );
          })()}


          {/* Metrics Grid */}
          <div className="dashboard-metrics-grid" style={{ gridTemplateColumns: '1fr' }}>
            
            {/* Metric Card: Assigned Projects */}
            <div className="card dashboard-metric-card">
              <div className="dashboard-metric-icon-box project">
                <FolderGit2 size={26} />
              </div>
              <div>
                <div className="dashboard-metric-label">Assigned Projects</div>
                <div className="dashboard-metric-value">
                  {onboardingStatus?.assigned_projects_count || 0} Active
                </div>
                <div className="dashboard-metric-subtitle blue">
                  AI Mentorship Ready
                </div>
              </div>
            </div>

          </div>

          {/* Quick Action Navigation Grid */}
          <h2 className="dashboard-section-title">
            Joinee Workspaces & AI Mentors
          </h2>

          <div className="dashboard-modules-grid">

            {/* Step 1: Company Policies & HR Assistance */}
            <div className="card dashboard-module-card">
              <div className="dashboard-module-header">
                <span className="badge badge-info">HR Policy Desk</span>
                {hrProfile?.avatar_url || hrProfile?.avatar ? (
                  <div className="dashboard-hr-avatar-mini">
                    <img src={hrProfile?.avatar_url || hrProfile?.avatar} alt="HR Avatar" className="dashboard-hr-avatar-img" />
                  </div>
                ) : (
                  <Bot size={22} style={{ color: '#4f46e5' }} />
                )}
              </div>
              <h3 className="dashboard-module-title">
                Company Policies & HR Assistance
              </h3>
              <p className="dashboard-module-desc">
                "Hi! I'm here. Have any doubts about company policies, leave rules, or timings? Ask me anytime!"
              </p>
              <button
                className="btn btn-primary dashboard-module-btn"
                onClick={() => navigate('/company-policies/chat')}
              >
                <span>I'm here! Have doubts? Ask with me</span>
                <ArrowRight size={16} />
              </button>
            </div>

            {/* Step 2: My Projects & AI Mentor */}
            <div className="card dashboard-module-card">
              <div className="dashboard-module-header">
                <span className="badge badge-success">Project Mentoring</span>
                {employee?.department_detail?.avatar_image ? (
                  <div className="dashboard-hr-avatar-mini" style={{ borderColor: '#059669' }}>
                    <img src={employee.department_detail.avatar_image} alt="TL Avatar" className="dashboard-hr-avatar-img" />
                  </div>
                ) : (
                  <Bot size={22} style={{ color: '#059669' }} />
                )}
              </div>
              <h3 className="dashboard-module-title">
                My Projects & AI Project Mentor
              </h3>
              <p className="dashboard-module-desc">
                Review technical workflows, architecture docs, and ask AI questions specific to your assigned project repositories.
              </p>
              <button
                className="btn btn-secondary dashboard-module-btn"
                onClick={() => navigate('/my-projects')}
              >
                <span>View Assigned Projects</span>
                <ArrowRight size={16} />
              </button>
            </div>

          </div>

        </main>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
